import type { TicketPriority } from '@esa/shared';
import { Chilli } from './Chilli.js';
import { Tooltip } from './Overlays.js';
import { cn } from './cn.js';

/**
 * How urgent somebody decided this is, on a heat scale.
 *
 * COUNTABLE, NOT JUST COLOURED. Four levels drawn as four differently
 * coloured marks are four things a colour-blind reader cannot tell apart,
 * and this sits in a dense list where every row competes for the same
 * attention. One chilli, two, three is a scale people already read off a
 * menu without a legend, and it survives greyscale, a screenshot, and being
 * described down a phone.
 *
 * `low` DRAWS NOTHING. It is the default and most tickets are it, so a mark
 * on every row would be noise carrying no information — and worse, it would
 * make the rows that do mean something harder to find. The absence is the
 * signal.
 */
const HEAT: Record<Exclude<TicketPriority, 'low'>, { n: number; className: string; why: string }> = {
  medium: {
    n: 1,
    className: 'text-ink-secondary',
    why: 'Slightly ahead of ordinary work.',
  },
  high: {
    n: 2,
    className: 'text-warn',
    why: 'Ahead of ordinary work, but not pinned.',
  },
  critical: {
    n: 3,
    className: 'text-danger',
    why: 'Pinned to the top of every list until someone lowers it.',
  },
};

export const PRIORITY_LABEL: Record<TicketPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

function Peppers({ n, className }: { n: number; className?: string }) {
  return (
    <span className={cn('inline-flex shrink-0 items-center', className)}>
      {Array.from({ length: n }, (_, i) => (
        // Overlapped slightly: three separate icons read as three things,
        // a tight cluster reads as one rating.
        //
        // 3px, not the 6px the old filled chilli used. That one was a solid
        // pod and could be crowded; this one is an outline, and at 6px the
        // three run together into a shape you have to stop and count. 3px is
        // the width at which they still read as one rating but a glance can
        // tell two from three — which is the entire job of the badge.
        <Chilli key={i} className={cn('h-4 w-4', i > 0 && '-ml-[3px]')} />
      ))}
    </span>
  );
}

export function PriorityIcon({
  priority,
  className,
}: {
  priority: TicketPriority;
  className?: string;
}) {
  if (priority === 'low') return null;
  const heat = HEAT[priority];
  return (
    <Tooltip content={`${PRIORITY_LABEL[priority]} — ${heat.why}`}>
      <span className={cn('inline-flex items-center', heat.className, className)}>
        <Peppers n={heat.n} />
        {/* The word, for anyone who cannot see the shape or the colour. A
            screen reader announcing "image, image, image" is worse than no
            icon at all. */}
        <span className="sr-only">{PRIORITY_LABEL[priority]} priority</span>
      </span>
    </Tooltip>
  );
}

/** Peppers plus the word, for screens with room for both. */
export function PriorityLabel({ priority }: { priority: TicketPriority }) {
  if (priority === 'low') {
    return <span className="text-xs text-ink-tertiary">Low</span>;
  }
  const heat = HEAT[priority];
  return (
    <span className={cn('inline-flex items-center gap-1 text-xs font-medium', heat.className)}>
      <Peppers n={heat.n} />
      {PRIORITY_LABEL[priority]}
    </span>
  );
}

/**
 * The ladder, hottest first.
 *
 * Critical at the top because a menu is read downwards and the reason
 * somebody opened it is almost always to raise something, not lower it.
 */
export const PRIORITY_OPTIONS: { value: TicketPriority; label: string }[] = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];
